import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetchUserPurchases } from '../lib/purchases';
import type { Bundle } from '../types/bundle';
import { useAuth } from './AuthContext';

interface PurchasesContextType {
  purchasedProductIds: Set<string>;
  hasPurchased: (productId: string) => boolean;
  hasPurchasedBundle: (bundle: Bundle) => boolean;
  loading: boolean;
}

const PurchasesContext = createContext<PurchasesContextType | undefined>(undefined);

export function PurchasesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;
  const [purchasedProductIds, setPurchasedProductIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(!!userId);

  useEffect(() => {
    if (!userId) {
      setPurchasedProductIds(new Set());
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchUserPurchases(userId)
      .then((rows) => {
        if (cancelled) return;
        const ids = new Set(rows.map((r) => r.product_id));
        setPurchasedProductIds(ids);
      })
      .catch(() => {
        if (!cancelled) setPurchasedProductIds(new Set());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const hasPurchased = useCallback(
    (productId: string) => purchasedProductIds.has(productId),
    [purchasedProductIds]
  );

  const hasPurchasedBundle = useCallback(
    (bundle: Bundle) => {
      if (bundle.productIds.length === 0) return false;
      return bundle.productIds.every((id) => purchasedProductIds.has(id));
    },
    [purchasedProductIds]
  );

  return (
    <PurchasesContext.Provider
      value={{
        purchasedProductIds,
        hasPurchased,
        hasPurchasedBundle,
        loading,
      }}
    >
      {children}
    </PurchasesContext.Provider>
  );
}

export function usePurchases() {
  const ctx = useContext(PurchasesContext);
  if (ctx === undefined) {
    throw new Error('usePurchases must be used within a PurchasesProvider');
  }
  return ctx;
}
